import type { AxiosResponse } from 'axios';
import { useMutation, useQuery } from 'react-query';

import V2Error from '@/app/V2Error';
import api from '@/services/api/v2';
import { userKey } from '@/services/queryKey';
import { getTokens } from '@/utils/auth-fns';

interface PasswordPayload {
  password: string;
  newPassword: string;
}

interface UserId {
  userId: number;
}
interface PhonePayload extends UserId {
  phone: string;
}

interface ProfileImagePayload {
  file: number;
}

export const userService = {
  fetchUserDetail: async () => {
    try {
      const { data } = await api.get('/users/me');
      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },
  updatePassword: async (password: PasswordPayload) => {
    const { data } = await api.patch('/users/password', password);
    return data;
  },
  updatePhone: async ({ userId, phone }: PhonePayload) => {
    const { data } = await api.patch(`/users/${userId}/phone`, { phone });
    return data;
  },
  updateProfileImage: async (image: ProfileImagePayload) => {
    const { data } = await api.patch('/users/crew/profile-image', image); // FIXME: /users/manager/profile-image
    return data;
  },
};

export function useFetchUserDetail() {
  const tokens = getTokens();

  const { data } = useQuery<User.Detail, V2Error>(
    userKey.detailByToken(tokens?.accessToken),
    () => userService.fetchUserDetail(),
    {
      enabled: Boolean(tokens),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    }
  );

  return data;
}

export function useAsyncUpdateUserPassword(
  onSuccess?: (res: AxiosResponse) => void,
  onError?: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<AxiosResponse, V2ErrorType, PasswordPayload>(
    (password) => userService.updatePassword(password),
    {
      onSuccess(res) {
        onSuccess && onSuccess(res);
      },
      onError(error) {
        onError && onError(error);
      },
    }
  );

  return mutate;
}

export function useAsyncUpdateUserPhone(
  onSuccess?: (res: AxiosResponse) => void,
  onError?: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<AxiosResponse, V2ErrorType, PhonePayload>(
    (payload) => userService.updatePhone(payload),
    {
      onSuccess(res) {
        onSuccess && onSuccess(res);
      },
      onError(error) {
        onError && onError(error);
      },
    }
  );

  return mutate;
}

export function useAsyncUpdateUserProfileImage(
  onSuccess?: (res: AxiosResponse) => void,
  onError?: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<
    AxiosResponse,
    V2ErrorType,
    ProfileImagePayload
  >((payload) => userService.updateProfileImage(payload), {
    onSuccess(res) {
      onSuccess && onSuccess(res);
    },
    onError(error) {
      onError && onError(error);
    },
  });

  return mutate;
}
